import { EntityModelJson } from './api'

// Normalizes a raw entity-model blob (model_data) into the flat EntityModelJson
// shape that EntityModelColumns renders. Handles both the branch-based export
// (branches[].entities[].content[...] + nested sub_nodes) and an already-flat
// blob (top-level entities[]).

// Splits "Key: value" / "Verb → [targets]" attribute strings into a { key: value }
// map so Object.entries(...) rendering in the viz shows labelled rows. Arrays of
// strings are the common shape; an object is passed through; anything else → {}.
function toAttrObject(value: unknown): Record<string, string> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, string>
  }
  if (!Array.isArray(value)) return {}

  const out: Record<string, string> = {}
  value.forEach((item, i) => {
    const s = String(item)
    // Earliest of these delimiters separates the label from the value.
    let idx = -1
    let dlen = 0
    for (const d of ['→', '->', ':']) {
      const at = s.indexOf(d)
      if (at > 0 && (idx === -1 || at < idx)) {
        idx = at
        dlen = d.length
      }
    }
    let key: string
    let val: string
    if (idx > 0) {
      key = s.slice(0, idx).trim()
      val = s.slice(idx + dlen).trim()
    } else {
      key = String(i + 1)
      val = s
    }
    // Guard against duplicate labels within one entity (e.g. two "Requires").
    if (key in out) key = `${key} (${i + 1})`
    out[key] = val
  })
  return out
}

export function toFlatEntityModel(raw: Record<string, any> | null | undefined): EntityModelJson {
  const empty: EntityModelJson = { company: '', total_entities: 0, entities: [] }
  if (!raw || typeof raw !== 'object') return empty

  const company = typeof raw.company === 'string' ? raw.company : ''

  // Already flat.
  if (Array.isArray(raw.entities)) {
    return {
      company,
      total_entities: typeof raw.total_entities === 'number' ? raw.total_entities : raw.entities.length,
      entities: raw.entities,
    }
  }

  // Branch-based: walk branches → entities → sub_nodes, emitting one flat entity
  // per node that carries a content object. Section-header nodes (content === "")
  // are skipped but still recursed into for their sub_nodes.
  if (Array.isArray(raw.branches)) {
    const entities: Record<string, any>[] = []

    for (const branch of raw.branches) {
      const branchId = branch?.branch_id != null ? String(branch.branch_id) : undefined
      let seq = 0

      const walk = (nodes: unknown) => {
        if (!Array.isArray(nodes)) return
        for (const node of nodes) {
          if (!node || typeof node !== 'object') continue
          const content = (node as any).content
          if (content && typeof content === 'object' && !Array.isArray(content)) {
            seq += 1
            entities.push({
              name: (node as any).name,
              entity_type: content['Entity Type'] ?? 'Other',
              description: content['Description'] ?? '',
              key_attributes: toAttrObject(content['Key Attributes']),
              relationships: toAttrObject(content['Relationships']),
              // Per-node id keeps React keys / side-panel selection unique even
              // though the source only numbers at the branch level.
              branch_id: branchId !== undefined ? `${branchId}.${seq}` : undefined,
            })
          }
          walk((node as any).sub_nodes)
        }
      }

      walk(branch?.entities)
    }

    return {
      company,
      total_entities: typeof raw.total_entities === 'number' ? raw.total_entities : entities.length,
      entities,
    }
  }

  return { ...empty, company }
}
